import { calculateDaysDifference } from '@/components/Modals/AddTask/AddTask.utils';
import { _isEmpty, _isEmptyString, _isNil } from '@/littledash';
import { isDateGreaterThan, isPositiveInteger } from '@/utils/forms/validators';
import type { RepeatUnit, RepeatValue } from '@/model/Task.model';

export const repeatPeriodOptions: Record<RepeatUnit, { label: string; value: RepeatUnit }> = {
  days: { label: 'Day(s)', value: 'days' as RepeatUnit },
  weeks: { label: 'Week(s)', value: 'weeks' as RepeatUnit },
};

export const daysOptions = [
  { label: 'Monday', value: 'monday' },
  { label: 'Tuesday', value: 'tuesday' },
  { label: 'Wednesday', value: 'wednesday' },
  { label: 'Thursday', value: 'thursday' },
  { label: 'Friday', value: 'friday' },
  { label: 'Saturday', value: 'saturday' },
  { label: 'Sunday', value: 'sunday' },
];

export const oneOffDateValidator = (value?: string): boolean | string => {
  if (_isNil(value) || _isEmptyString(value)) {
    return 'Date is required';
  }
  return true;
};

export const startDateValidator = (startDate?: string, endDate?: string): boolean | string => {
  if (_isNil(startDate) || _isEmptyString(startDate)) {
    return 'A start date is required';
  }
  if (_isNil(endDate) || _isEmptyString(endDate)) {
    return 'An end date is required';
  }
  if (isDateGreaterThan(startDate, endDate)) {
    return 'Start date must be before end date';
  }

  if (calculateDaysDifference(startDate, endDate) > 365) {
    return 'Start date must be within 1 year of end date';
  }
  return true;
};

export const repeatValidator = (value?: Omit<RepeatValue, 'on_day'>): boolean | string => {
  const repeatOptionKeys = Object.keys(repeatPeriodOptions);
  if (_isNil(value?.value)) {
    return 'A repeat value is required';
  }
  if (_isNil(value?.unit)) {
    return 'A repeat unit is required';
  }
  if (!isPositiveInteger(value.value)) {
    return 'Value must be a positive number';
  }
  if (!repeatOptionKeys.includes(value.unit)) {
    return `Unit must be one of ${repeatOptionKeys.join(',')}`;
  }
  return true;
};

export const recurringDayValidator = (value?: string[] | null): boolean | string => {
  if (_isEmpty(value) || _isNil(value)) {
    return 'At least one day is required';
  }
  return true;
};

export const conditionalValueValidator = (value: Array<number> | undefined): boolean | string => {
  if (_isNil(value) || _isEmpty(value)) {
    return 'At least one value is required';
  }
  return true;
};

const DAY_MAX = 1460;
const DAY_MIN = -365;

export const conditionalAnimalAgeValidator = (ageValues: Array<number> | undefined) => {
  if (!ageValues) {
    return 'At least one value is required';
  }
  if (ageValues.some((age) => !Number.isInteger(age) || age < 0 || age > DAY_MAX)) {
    return `Age must be a whole number between 0 and ${DAY_MAX} days`;
  }
  return true;
};

export const conditionalStudyDayValidator = (days: Array<number>, trackingDayBase?: number) => {
  const offset = trackingDayBase ?? 0;
  if (!days) {
    return 'At least one value is required';
  }
  if (offset === -1) {
    if (days.some((day) => !Number.isInteger(day) || day < DAY_MIN || day > DAY_MAX)) {
      return `Study day must be a whole number between ${DAY_MIN} and ${DAY_MAX}`;
    }
  } else if (offset === 1) {
    if (days.some((day) => !Number.isInteger(day) || day < DAY_MIN || day === 0 || day === 1 || day > DAY_MAX)) {
      return `Study day must be a whole number between ${DAY_MIN} and ${DAY_MAX}, and can't be 0 or 1`;
    }
  } else {
    if (days.some((day) => !Number.isInteger(day) || day < DAY_MIN || day === 0 || day > DAY_MAX)) {
      return `Study day must be a whole number between ${DAY_MIN} and ${DAY_MAX}, and can't be 0`;
    }
  }
  return true;
};

export const conditionalTrackingDayValidator = (days: Array<number> | undefined, trackingDayBase?: number) => {
  if (!days) {
    return 'At least one value is required';
  }
  if (trackingDayBase === 1) {
    if (days.some((day) => !Number.isInteger(day) || day < DAY_MIN || day === 0 || day > DAY_MAX)) {
      return `Tracking day must be a whole number between ${DAY_MIN} and ${DAY_MAX}, and can't be 0`;
    }
  } else if (days.some((day) => !Number.isInteger(day) || day < DAY_MIN || day > DAY_MAX)) {
    return `Tracking day must be a whole number between ${DAY_MIN} and ${DAY_MAX}`;
  }
  return true;
};
